"use client";

/**
 * Chart-local Market Depth (DOM) panel.
 *
 * Performance contract:
 *  - The component itself only re-renders when the symbol changes or its
 *    `status` flips between `subscribing` / `subscribed` / `unavailable` / `error`.
 *  - Streaming book updates DO NOT trigger React state updates in the chart.
 *    A stable pool of row refs is mutated imperatively via Zustand's
 *    `subscribe` API, batched in `requestAnimationFrame`.
 *  - The panel never reads chart instances or candle state, so it cannot
 *    cause the chart to repaint.
 */

import React, { useEffect, useMemo, useRef } from "react";
import { LuX } from "react-icons/lu";
import { useMarketDepthStore } from "@/vybe-trader-client/order-book";
import { SkeletonRows } from "@/components/common/SkeletonRows";
import { EmptyBlock } from "@/components/common/EmptyBlock";
import { ErrorBlock } from "@/components/common/ErrorBlock";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";

const DOM_SKELETON = { columnWidths: [60, 40, 52], rowCount: 8 } as const;
import type {
  BookBar,
  MarketDepthSnapshot,
  MarketDepthSubscriptionStatus,
} from "@/vybe-trader-client/order-book";

const ROW_COUNT = 10;

interface ChartMarketDepthPanelProps {
  symbol: string;
  digits?: number;
  /** Total visible rows per side. Defaults to 10. */
  rowCount?: number;
  width?: number | string;
  isDesktop?: boolean;
  onClose?: () => void;
}

interface RowRefs {
  root: HTMLDivElement | null;
  volume: HTMLSpanElement | null;
  lots: HTMLSpanElement | null;
  price: HTMLSpanElement | null;
}

const formatPrice = (price: number, digits: number): string =>
  Number.isFinite(price) ? price.toFixed(digits) : "";

const formatLots = (lots: number): string => {
  if (!Number.isFinite(lots)) return "";
  if (lots >= 100) return lots.toFixed(0);
  if (lots >= 10) return lots.toFixed(1);
  return lots.toFixed(2);
};

const formatVolume = (volume: number): string =>
  Number.isFinite(volume) ? volume.toLocaleString("en-US") : "";

const getDepthValue = (bar: BookBar): number =>
  Number.isFinite(bar.lots) && bar.lots > 0 ? bar.lots : Number(bar.volume) || 0;

function applyRow(
  refs: RowRefs,
  bar: BookBar | undefined,
  digits: number,
  side: "ask" | "bid",
  maxDepth: number
): void {
  const root = refs.root;
  if (!root) return;

  if (!bar) {
    if (root.dataset.empty !== "1") {
      root.dataset.empty = "1";
      root.style.opacity = "0.25";
    }
    if (refs.volume && refs.volume.textContent !== "") refs.volume.textContent = "";
    if (refs.lots && refs.lots.textContent !== "") refs.lots.textContent = "";
    if (refs.price && refs.price.textContent !== "") refs.price.textContent = "";
    root.style.background = "transparent";
    return;
  }

  if (root.dataset.empty === "1") {
    root.dataset.empty = "0";
    root.style.opacity = "1";
  }

  const volume = formatVolume(bar.volume);
  const lots = formatLots(bar.lots);
  const price = formatPrice(bar.price, digits);
  const depth = getDepthValue(bar);
  const depthPercent =
    maxDepth > 0 ? Math.max(4, Math.min(100, (depth / maxDepth) * 100)) : 0;
  // Token-driven depth fill: ask uses --destructive, bid uses --primary,
  // both at the same 0.18 alpha as before (theme-aware, 1:1 with tokens.css).
  const depthColor =
    side === "ask"
      ? "hsl(var(--destructive) / 0.18)"
      : "hsl(var(--primary) / 0.18)";
  root.style.background = `linear-gradient(to right, ${depthColor} 0 ${depthPercent}%, transparent ${depthPercent}% 100%)`;

  if (refs.volume && refs.volume.textContent !== volume) {
    refs.volume.textContent = volume;
  }
  if (refs.lots && refs.lots.textContent !== lots) {
    refs.lots.textContent = lots;
  }
  if (refs.price && refs.price.textContent !== price) {
    refs.price.textContent = price;
  }
}

export default function ChartMarketDepthPanel({
  symbol,
  digits = 5,
  rowCount = ROW_COUNT,
  width,
  isDesktop = false,
  onClose,
}: ChartMarketDepthPanelProps) {
  // Only subscribe to subscription topology (status), NOT the streaming snapshot.
  const status = useMarketDepthStore((state) =>
    state.bySymbol.get(symbol)?.status ?? "idle"
  ) as MarketDepthSubscriptionStatus;
  const errorMessage = useMarketDepthStore(
    (state) => state.bySymbol.get(symbol)?.errorMessage ?? null
  );
  // Has the store ever received any data for this symbol? Used so we can
  // render a stale-but-instant cache while a fresh subscription is still
  // negotiating (avoids the "Subscribing..." flash on every symbol revisit).
  const hasCachedSnapshot = useMarketDepthStore(
    (state) => state.bySymbol.get(symbol)?.snapshot !== undefined
  );

  const { client } = useVybeTraderClient();

  // Stable row ref pool.
  const askRefs = useRef<RowRefs[]>(
    Array.from({ length: rowCount }, () => ({
      root: null,
      volume: null,
      lots: null,
      price: null,
    }))
  );
  const bidRefs = useRef<RowRefs[]>(
    Array.from({ length: rowCount }, () => ({
      root: null,
      volume: null,
      lots: null,
      price: null,
    }))
  );
  const spreadRef = useRef<HTMLSpanElement | null>(null);

  // Keep callbacks/effect inputs stable to avoid resubscribing the store on every render.
  const renderRequestedRef = useRef(false);
  const lastSeenVersionRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const apply = () => {
      renderRequestedRef.current = false;
      if (cancelled) return;
      const snapshot = useMarketDepthStore.getState().getSnapshot(symbol);
      paint(snapshot);
    };

    const paint = (snapshot: MarketDepthSnapshot | undefined) => {
      if (!snapshot) {
        for (let i = 0; i < rowCount; i++) {
          applyRow(askRefs.current[i], undefined, digits, "ask", 0);
          applyRow(bidRefs.current[i], undefined, digits, "bid", 0);
        }
        if (spreadRef.current) spreadRef.current.textContent = "";
        return;
      }

      // Asks: best (lowest) at the BOTTOM of the asks block (closest to price center)
      const asks = snapshot.asks;
      const askDisplay = asks.slice(0, rowCount).reverse();
      const askMaxDepth = Math.max(0, ...askDisplay.map(getDepthValue));
      for (let i = 0; i < rowCount; i++) {
        const offset = rowCount - askDisplay.length;
        const bar = i >= offset ? askDisplay[i - offset] : undefined;
        applyRow(askRefs.current[i], bar, digits, "ask", askMaxDepth);
      }

      // Bids: best (highest) at the TOP of the bids block (closest to price center)
      const bids = snapshot.bids;
      const bidDisplay = bids.slice(0, rowCount);
      const bidMaxDepth = Math.max(0, ...bidDisplay.map(getDepthValue));
      for (let i = 0; i < rowCount; i++) {
        const bar = bidDisplay[i];
        applyRow(bidRefs.current[i], bar, digits, "bid", bidMaxDepth);
      }

      if (spreadRef.current) {
        const bestBid = bids[0]?.price;
        const bestAsk = asks[0]?.price;
        if (
          typeof bestBid === "number" &&
          typeof bestAsk === "number" &&
          bestBid > 0 &&
          bestAsk > 0
        ) {
          const spread = bestAsk - bestBid;
          spreadRef.current.textContent = `Spread ${spread.toFixed(digits)}`;
        } else {
          spreadRef.current.textContent = "";
        }
      }
    };

    const initialSnapshot = useMarketDepthStore.getState().getSnapshot(symbol);
    lastSeenVersionRef.current = initialSnapshot?.version ?? 0;
    paint(initialSnapshot);

    const unsubscribe = useMarketDepthStore.subscribe((state) => {
      const snapshot = state.bySymbol.get(symbol)?.snapshot;
      if (!snapshot) {
        if (lastSeenVersionRef.current !== 0) {
          lastSeenVersionRef.current = 0;
          if (!renderRequestedRef.current) {
            renderRequestedRef.current = true;
            requestAnimationFrame(apply);
          }
        }
        return;
      }
      if (snapshot.version === lastSeenVersionRef.current) return;
      lastSeenVersionRef.current = snapshot.version;
      if (!renderRequestedRef.current) {
        renderRequestedRef.current = true;
        requestAnimationFrame(apply);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [symbol, digits, rowCount]);

  const rows = useMemo(
    () => Array.from({ length: rowCount }, (_, i) => i),
    [rowCount]
  );

  // Show the loading note ONLY when we have nothing useful to render.
  // If the store already has a cached snapshot for this symbol, paint the
  // ladder from cache immediately and let the live updates take over.
  const isUnavailable = status === "unavailable";
  const isError = status === "error" && !hasCachedSnapshot;
  const isLoading =
    !hasCachedSnapshot &&
    !isUnavailable &&
    !isError &&
    (status === "subscribing" || status === "idle");

  const headerText = isDesktop ? "DOM" : "Market depth";

  return (
    <div
      className="pointer-events-auto rounded-md border border-border bg-card text-card-foreground shadow-md"
      style={{
        width: width ?? (isDesktop ? 240 : "100%"),
        fontSize: 12,
        userSelect: "none",
      }}
    >
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b border-border">
        <div className="flex flex-col leading-tight">
          <span className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">
            {headerText}
          </span>
          <span className="text-[13px] font-semibold">{symbol}</span>
        </div>
        <div className="flex items-center gap-2">
          <span ref={spreadRef} className="text-[10px] text-muted-foreground" />
          {onClose && (
            <button
              onClick={onClose}
              disabled={isLoading}
              aria-disabled={isLoading}
              className="h-5 w-5 rounded hover:bg-muted flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title="Close market depth"
            >
              <LuX size={12} />
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="py-1">
          <SkeletonRows spec={DOM_SKELETON} />
        </div>
      )}

      {isUnavailable && (
        <EmptyBlock
          config={{
            glyph: "▦",
            message: "Market depth is not available for this symbol.",
          }}
        />
      )}

      {isError && (
        <ErrorBlock
          message={errorMessage ?? "Failed to subscribe to market depth."}
          onRetry={
            client
              ? () => {
                  void client.subscribeMarketDepth(symbol);
                }
              : undefined
          }
        />
      )}

      {!isLoading && !isUnavailable && !isError && (
        <div className="font-mono">
          <div className="px-2 py-1 grid grid-cols-[1fr_0.7fr_0.9fr] gap-2 text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border/50">
            <span className="text-left">Price</span>
            <span className="text-right">Lots</span>
            <span className="text-right">Volume</span>
          </div>

          <div>
            {rows.map((i) => (
              <div
                key={`ask-${i}`}
                ref={(el) => {
                  askRefs.current[i].root = el;
                }}
                className="grid grid-cols-[1fr_0.7fr_0.9fr] gap-2 px-2 py-0.5 items-center text-[11px] border-b border-border/30 transition-[opacity] duration-75"
              >
                <span
                  ref={(el) => {
                    askRefs.current[i].price = el;
                  }}
                  className="relative z-10 text-left font-semibold text-red-400"
                />
                <span
                  ref={(el) => {
                    askRefs.current[i].lots = el;
                  }}
                  className="relative z-10 text-right text-foreground/85"
                />
                <span
                  ref={(el) => {
                    askRefs.current[i].volume = el;
                  }}
                  className="relative z-10 text-right text-foreground/85"
                />
              </div>
            ))}
          </div>

          <div>
            {rows.map((i) => (
              <div
                key={`bid-${i}`}
                ref={(el) => {
                  bidRefs.current[i].root = el;
                }}
                className="grid grid-cols-[1fr_0.7fr_0.9fr] gap-2 px-2 py-0.5 items-center text-[11px] border-b border-border/30 transition-[opacity] duration-75"
              >
                <span
                  ref={(el) => {
                    bidRefs.current[i].price = el;
                  }}
                  className="relative z-10 text-left font-semibold text-blue-400"
                />
                <span
                  ref={(el) => {
                    bidRefs.current[i].lots = el;
                  }}
                  className="relative z-10 text-right text-foreground/85"
                />
                <span
                  ref={(el) => {
                    bidRefs.current[i].volume = el;
                  }}
                  className="relative z-10 text-right text-foreground/85"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
